/**
 * MasterActivityControls — toggle de modo de interação para o master.
 *
 * Botão único: "Assumir controle" ↔ "Liberar exploração".
 *
 * O mestre NÃO precisa de botões separados para destacar camadas — ele
 * interage com o próprio atlas (chips/canvas) e, em modo master-led, cada
 * clique dispara setActivity automaticamente via MissionSlide.
 *
 * Posição: desktop bottom-right, mobile top-right (evita colidir com
 * MasterControlsPill no bottom-left).
 */

import { useAdapter } from '../adapter'
import { useIsMobile } from '../hooks/useIsMobile'
import { useSessionState } from '../hooks/useSessionState'
import type { MissionSlide } from '../types/manifest'

interface Props {
  slide: MissionSlide
}

export function MasterActivityControls({ slide: _slide }: Props) {
  const adapter = useAdapter()
  const { interactionMode } = useSessionState()
  const isMobile = useIsMobile()

  const isMasterLed = interactionMode === 'master-led'

  const position: React.CSSProperties = isMobile
    ? { position: 'fixed', top: 12, right: 12, zIndex: 50 }
    : { position: 'fixed', bottom: 16, right: 16, zIndex: 50 }

  return (
    <button
      type="button"
      onClick={() =>
        adapter.setInteractionMode(isMasterLed ? 'free' : 'master-led')
      }
      title={
        isMasterLed
          ? 'Devolver a interação aos alunos'
          : 'Forçar sincronia: alunos verão o que você selecionar'
      }
      style={{
        ...position,
        minHeight: 44,
        padding: '10px 16px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: isMasterLed
          ? 'var(--color-lab-accent, #5B2DB8)'
          : 'var(--color-lab-mint, #1E9E6A)',
        color: 'white',
        border: 'none',
        borderRadius: 'var(--radius-lab-sm, 6px)',
        fontFamily: 'var(--font-lab-mono, monospace)',
        fontSize: 'var(--text-lab-sm, 0.875rem)',
        fontWeight: 700,
        cursor: 'pointer',
        letterSpacing: 0.3,
        boxShadow: 'var(--lab-shadow, 0 8px 24px rgba(15,17,21,0.08))',
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 14 }}>
        {isMasterLed ? '🔒' : '🔓'}
      </span>
      <span>{isMasterLed ? 'Liberar exploração' : 'Assumir controle'}</span>
    </button>
  )
}
